/**
 * API client for Companies (parties_company table)
 * Used for incident customer lookups (customerType='company')
 */

import { apiRequest } from './standard-api';

export interface Company {
  id: string;
  tenantId?: string;
  taxIdHash?: string;
  legalName: string;
  tradeName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  industry?: string | null;
  industryId?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  region?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  ownerUserId?: string | null;
  owningTeamId?: string | null;
  owningBuId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CompanyListResponse {
  companies: Company[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface CompanyListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * List companies with optional filtering and pagination
 * Used for lookup/search in incident forms
 */
export async function listCompanies(params: CompanyListParams = {}): Promise<{ data: Company[], total: number, pagination: any }> {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.set('page', String(params.page));
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.search) queryParams.set('search', params.search);
  if (params.sortBy) queryParams.set('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

  const response = await apiRequest<Company[]>(
    `/api/v1/companies${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to load companies');
  }

  return {
    data: response.data,
    total: (response.meta as any)?.pagination?.total || 0,
    pagination: (response.meta as any)?.pagination || {}
  };
}

/**
 * Get company by ID
 */
export async function getCompany(id: string): Promise<Company> {
  const response = await apiRequest<Company>(
    `/api/v1/companies/${id}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to load company');
  }

  return response.data;
}

/**
 * Search companies by name or tax ID (for autocomplete)
 */
export async function searchCompanies(query: string, limit: number = 10): Promise<Company[]> {
  const response = await listCompanies({
    search: query,
    limit,
    sortBy: 'legalName',
    sortOrder: 'asc'
  });

  return response.data;
}

/**
 * Create new company
 */
export async function createCompany(data: Partial<Company>): Promise<Company> {
  const response = await apiRequest<Company>(
    `/api/v1/companies`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );

  if (!response.data) {
    throw new Error('Failed to create company');
  }

  return response.data;
}

/**
 * Update company by ID
 */
export async function updateCompany(id: string, data: Partial<Company>): Promise<Company> {
  const response = await apiRequest<Company>(
    `/api/v1/companies/${id}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );

  if (!response.data) {
    throw new Error('Failed to update company');
  }

  return response.data;
}

/**
 * Delete company by ID (soft delete)
 */
export async function deleteCompany(id: string): Promise<void> {
  await apiRequest<void>(
    `/api/v1/companies/${id}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

/**
 * Archive company
 * @param companyId - Company ID to archive
 * @param reason - Optional reason for archiving
 * @returns Updated company record
 */
export async function archiveCompany(companyId: string, reason?: string): Promise<Company> {
  const response = await apiRequest<Company>(
    `/api/v1/companies/${companyId}/archive`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );

  if (!response.data) {
    throw new Error('Failed to archive company');
  }

  return response.data;
}

/**
 * Unarchive company
 * @param companyId - Company ID to unarchive
 * @returns Updated company record
 */
export async function unarchiveCompany(companyId: string): Promise<Company> {
  const response = await apiRequest<Company>(
    `/api/v1/companies/${companyId}/unarchive`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to unarchive company');
  }

  return response.data;
}

/**
 * PROJ-038: Bulk delete multiple companies at once
 */
export interface BulkDeleteResponse {
  deleted: number;
  failed: string[];
  total: number;
}

export async function bulkDeleteCompanies(ids: string[]): Promise<BulkDeleteResponse> {
  const response = await apiRequest<BulkDeleteResponse>(
    `/api/v1/companies/bulk`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    }
  );

  if (!response.data) {
    throw new Error('Failed to bulk delete companies');
  }

  return response.data;
}
